import { useState, useEffect, useRef } from "react";
import api from "../../utils/api";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  startDraftBotSession,
  isDraftThreadId,
} from "../../pages/UserDashboard/utils/addThreadActions";
import {
  renameDeepResearch,
  deleteDeepResearch,
} from "../../pages/UserDashboard/utils/deepResearchHelper";
import mainLogo from "../../assets/babylon.png";
import { setCachedHistory } from "./cache/caching";
import {
  CircleUser,
  Ellipsis,
  FileText,
  KeyRound,
  LogOut,
  PanelLeftClose,
  Pencil,
  Sparkle,
  Telescope,
  Trash2,
  Zap,
} from "lucide-react";

export default function SideBar({
  userLevel,
  token,
  history,
  setHistory,
  threadId,
  botRole,
  setThread,
  setThreadId,
  setBotRole,
  setMessages,
  logout,
  sidebarVisible,
  setSidebarVisible,
  openModal,
  wait,
  fetchThreadMessages,
  loading,
  setLoading,
  darkMode,
  setDarkMode,
  handleChangePassword,
  onStartDeepResearch,
  onOpenDeepResearchThread,
  setDeepResearchConversationId,
  removeDeepResearchDraft,
}) {
  const [, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileDropdown, setProfileDropdown] = useState(null);
  const [renamingThreadId, setRenamingThreadId] = useState(null);
  const [newThreadName, setNewThreadName] = useState("");
  const dropdownRef = useRef();
  const buttonRef = useRef();
  const user = JSON.parse(sessionStorage.getItem("user"));
  const responsesApiBots = [
    {
      botId: 1,
      title: "New Chat",
      label: "General Chat",
      Icon: Sparkle,
      buttonClass:
        "text-green-800 hover:bg-green-200 dark:text-green-300 dark:hover:bg-green-900/70",
    },
    {
      botId: 2,
      title: "New Chat",
      label: "Electric Coop Chat",
      Icon: Zap,
      buttonClass:
        "text-yellow-800 hover:bg-yellow-200 dark:text-yellow-300 dark:hover:bg-yellow-900/70",
    },
    {
      botId: 3,
      title: "New Chat",
      label: "Invoicing Checker Chat",
      Icon: FileText,
      buttonClass:
        "text-blue-800 hover:bg-blue-200 dark:text-blue-300 dark:hover:bg-blue-900/70",
    },
  ];

  const startResponsesApiSession = ({ botId, title }) => {
    if (isFetching || wait) return;
    setDeepResearchConversationId?.(null);
    removeDeepResearchDraft?.();
    startDraftBotSession({
      botId,
      title,
      setThread,
      setThreadId,
      setBotRole,
      setMessages,
      setHistory,
      setSidebarVisible,
    });
  };
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileDropdown(false);
        setDropdownOpen(false);
      }
    };
    if (profileDropdown || dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdown, dropdownOpen]);

  const toggleButton = () => {
    setProfileDropdown((prevState) => !prevState);
  };

  const formatPeriodLabel = (period) => {
    switch (period) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "last7Days":
        return "Last 7 Days";
      case "last14Days":
        return "Last 14 Days";
      case "last30Days":
        return "Last 30 Days";
      case "older":
        return "Older";
      default:
        return period;
    }
  };

  const handleThreadClick = async (threadId, thread, bot_role, item) => {
    if (loading || isFetching || wait) return;

    if (bot_role === "deep_research") {
      onOpenDeepResearchThread?.(threadId, item?.name);
      return;
    }
    setDeepResearchConversationId?.(null);
    removeDeepResearchDraft?.();

    if (isDraftThreadId(threadId)) {
      setMessages([]);
      setThread(thread);
      setThreadId(threadId);
      setBotRole(bot_role);
      return;
    }

    const cachedMessages = JSON.parse(
      localStorage.getItem(`messages_${threadId}`),
    );

    if (cachedMessages) {
      localStorage.setItem("threadId", threadId);
      setMessages(cachedMessages);
      setThread(thread);
      setThreadId(threadId);
      setBotRole(bot_role);
      return;
    }

    setThread(thread);
    setThreadId(threadId);
    setBotRole(bot_role);
    localStorage.setItem("threadId", threadId);
    if (bot_role === "responses_api") {
      fetchThreadMessages(threadId, bot_role);
    }
    setIsFetching(false);
  };

  {
    /*Below this handles the renaming of the thread from caching to calling of api*/
  }
  const handleRename = (threadId) => {
    setRenamingThreadId(threadId);
    const thread = Object.values(history)
      .flat()
      .find((item) => item.thread_id === threadId);
    setNewThreadName(thread ? thread.name : "");
  };

  const submitRename = async (threadId, botRole) => {
    try {
      if (botRole === "deep_research") {
        await renameDeepResearch(token, threadId, newThreadName);
      } else {
        await api.patch(
          `/v2/responses-chat/conversation/${threadId}/title`,
          { title: newThreadName },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      }
      renameThread(threadId, newThreadName);
      setRenamingThreadId(null);
      setRefresh(!refresh);
    } catch (error) {
      setError("Error renaming thread");
      console.error("Error renaming thread:", error);
    }
  };
  const renameThread = (threadId, newName) => {
    const updatedHistory = { ...history };
    Object.keys(updatedHistory).forEach((period) => {
      updatedHistory[period] = updatedHistory[period].map((thread) => {
        if (thread.thread_id === threadId) {
          return { ...thread, name: newName };
        }
        return thread;
      });
    });

    // Update the state and cache
    setHistory(updatedHistory);
    setCachedHistory(updatedHistory);
  };

  const getThreadTimestamp = (item) => {
    const timestamp = item?.updated_at || item?.last_used || item?.created_at;
    const parsed = timestamp ? new Date(timestamp).getTime() : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const selectNewestThread = (updatedHistory) => {
    const newestThread = Object.values(updatedHistory || {})
      .flat()
      .filter(Boolean)
      .sort((a, b) => getThreadTimestamp(b) - getThreadTimestamp(a))[0];

    if (!newestThread) {
      localStorage.removeItem("threadId");
      sessionStorage.removeItem("threadId");
      startDraftBotSession({
        botId: 1,
        title: "New Chat",
        setThread,
        setThreadId,
        setBotRole,
        setMessages,
        setHistory,
        setSidebarVisible,
      });
      return;
    }

    const nextThreadId = newestThread.thread_id;
    const nextBotRole = newestThread.bot_role;

    if (nextBotRole === "deep_research") {
      onOpenDeepResearchThread?.(nextThreadId, newestThread.name);
      return;
    }
    setDeepResearchConversationId?.(null);
    removeDeepResearchDraft?.();

    if (isDraftThreadId(nextThreadId)) {
      setThread(null);
      setThreadId(nextThreadId);
      setBotRole(nextBotRole);
      setMessages([]);
      return;
    }

    const cachedMessages = JSON.parse(
      localStorage.getItem(`messages_${nextThreadId}`) || "null",
    );

    setThread(newestThread.thread || null);
    setThreadId(nextThreadId);
    setBotRole(nextBotRole);
    localStorage.setItem("threadId", nextThreadId);
    sessionStorage.setItem("threadId", nextThreadId);

    if (cachedMessages) {
      setMessages(cachedMessages);
      return;
    }

    setMessages([]);
    if (nextBotRole === "responses_api") {
      fetchThreadMessages(nextThreadId, nextBotRole);
    }
  };

  const removeDeletedThread = (deletedThreadId) => {
    const deletedId = deletedThreadId?.toString();
    const updatedHistory = Object.fromEntries(
      Object.entries(history || {}).map(([period, items]) => [
        period,
        (items || []).filter(
          (item) => item.thread_id?.toString() !== deletedId,
        ),
      ]),
    );

    setHistory(updatedHistory);
    setCachedHistory(updatedHistory);
    localStorage.removeItem(`messages_${deletedThreadId}`);

    if (threadId?.toString() === deletedId) {
      selectNewestThread(updatedHistory);
    }
  };

  const handleDelete = async (threadId, threadName, botRole) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        const deleteThread = async () => {
          try {
            if (botRole === "deep_research") {
              await deleteDeepResearch(token, threadId);
            } else {
              await api.delete(`/v2/responses-chat/conversation/${threadId}`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
            }
            removeDeletedThread(threadId);
          } catch (error) {
            console.error("Error deleting thread:", error);
          } finally {
            onClose();
          }
        };

        return (
          <div className="p-4 text-center rounded-lg shadow-lg shadow-black/25 ring-1 bg-slate-100 w-96 dark:bg-slate-900 dark:ring-slate-700">
            <h1 className="mb-2 text-lg font-bold text-red-600 dark:text-red-400">
              Confirm Delete
            </h1>
            <div className="flex flex-col mb-4 text-gray-600 dark:text-gray-300">
              <p>Are you sure you want to delete this thread?</p>
              <p className="font-bold">"{threadName}"</p>
            </div>

            <div className="flex justify-center gap-4 pl-2">
              <button
                className="px-3 py-1.5 rounded-lg bg-slate-300 dark:bg-slate-600 dark:text-gray-100 hover:bg-slate-400 dark:hover:bg-slate-500"
                onClick={onClose}>
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-gray-100 bg-red-500 dark:bg-red-700 rounded-lg hover:bg-red-600 dark:hover:bg-red-600"
                onClick={deleteThread}>
                Delete
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleButtonClick = (e, action) => {
    e.stopPropagation();
    e.preventDefault();
    action();
    setDropdownOpen(null);
  };

  const getThreadBotColor = (item) => {
    if (item?.bot_role === "responses_api") {
      switch (Number(item?.bot_id)) {
        case 2:
          return "yellow";
        case 3:
          return "blue";
        case 1:
        default:
          return "green";
      }
    }

    if (item?.bot_role === "electric_coop") return "yellow";
    if (item?.bot_role === "invoicing") return "blue";
    if (item?.bot_role === "deep_research") return "orange";
    return "green";
  };

  const getThreadItemClasses = (item) => {
    if (loading) {
      return "cursor-not-allowed border-l-4 border-transparent text-gray-400 dark:text-gray-500";
    }

    // Responses-chat and Deep Research conversations come from separate
    // database tables, so their numeric ids can collide — bot_role must
    // also match, or two unrelated threads both light up as "active".
    const isActive =
      item.thread_id?.toString() === threadId?.toString() &&
      item.bot_role === botRole;
    const isDropdownOpen = dropdownOpen === item.thread_id;
    const botColor = getThreadBotColor(item);

    if (isDropdownOpen && !isActive) {
      return "border-l-4 border-slate-400 bg-slate-300 dark:border-slate-500 dark:bg-slate-700";
    }

    const colorClasses = {
      green: isActive
        ? "border-l-4 border-green-700 bg-green-400 font-semibold dark:border-green-300 dark:bg-green-900"
        : "border-l-4 border-green-500/70 text-green-950 hover:bg-green-200 dark:border-green-400/70 dark:text-green-100 dark:hover:bg-green-900/70",
      yellow: isActive
        ? "border-l-4 border-yellow-700 bg-yellow-300 font-semibold dark:border-yellow-300 dark:bg-yellow-500/70"
        : "border-l-4 border-yellow-500/80 text-yellow-950 hover:bg-yellow-200 dark:border-yellow-300/80 dark:text-yellow-100 dark:hover:bg-yellow-900/70",
      blue: isActive
        ? "border-l-4 border-blue-700 bg-blue-300 font-semibold dark:border-blue-300 dark:bg-sky-800"
        : "border-l-4 border-blue-500/70 text-blue-950 hover:bg-blue-200 dark:border-blue-300/70 dark:text-blue-100 dark:hover:bg-blue-900/70",
      orange: isActive
        ? "border-l-4 border-orange-700 bg-orange-300 font-semibold dark:border-orange-300 dark:bg-orange-800"
        : "border-l-4 border-orange-500/70 text-orange-950 hover:bg-orange-200 dark:border-orange-400/70 dark:text-orange-100 dark:hover:bg-orange-900/70",
    };

    return `${colorClasses[botColor]} cursor-pointer`;
  };

  const getLinkClasses = (id) => {
    return id === threadId ? "" : "";
  };

  return (
    <div
      className={`flex flex-col h-screen overflow-y-auto scrollbar-thin overflow-x-hidden transition-colors duration-100 ${
        darkMode ? "bg-slate-900 text-gray-100" : "bg-slate-200 text-gray-900"
      }`}>
      {/* Header Section */}
      <div className="sticky top-0 z-20 flex flex-col transition-colors duration-100 bg-slate-200 dark:bg-slate-900">
        <div className="flex justify-between top-0 px-2 items-center pt-2.5 pb-4">
          <img
            className="w-6 h-6 max-w-sm ml-2"
            src={mainLogo}
            alt="Logo"
          />
          <button
            className={`focus:outline-none transition-colors duration-100 p-1.5 dark:hover:bg-slate-700 hover:bg-slate-300 rounded-md  ${
              darkMode ? " hover:text-gray-300" : " hover:text-gray-700"
            }`}
            onClick={() => setSidebarVisible(!sidebarVisible)}>
            <PanelLeftClose size={20} />
          </button>
        </div>
        <div className="flex flex-col items-center justify-between w-full px-2 pt-1 pb-2 font-bold ">
          {responsesApiBots.map(
            ({ botId, title, label, Icon, buttonClass }) => (
              <button
                key={botId}
                onClick={() => startResponsesApiSession({ botId, title })}
                className={`flex items-center w-full gap-2 px-2 py-1 mb-1 transition-colors duration-100 rounded-md disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed ${buttonClass}`}
                disabled={isFetching}>
                <Icon size={20} />
                <span>{label}</span>
              </button>
            ),
          )}
          <button
            onClick={() => onStartDeepResearch?.()}
            className="flex items-center w-full gap-2 px-2 py-1 mb-1 text-orange-800 transition-colors duration-100 rounded-md disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed hover:bg-orange-200 dark:text-orange-300 dark:hover:bg-orange-900/70"
            disabled={isFetching}>
            <Telescope size={20} />
            <span>Deep Research</span>
          </button>
        </div>
      </div>
      {/* General Chat History Threads */}
      <ul className="flex-grow px-2 space-y-4 transition-colors duration-100 bg-inherit">
        {Object.entries(history).map(
          ([period, items]) =>
            items.length > 0 && (
              <div key={period}>
                <h3 className="mt-3 relative mb-2 ml-1.5 text-sm text-gray-500 dark:text-gray-400">
                  {formatPeriodLabel(period)}
                </h3>
                {items.map((item) => (
                  <li
                    key={item.thread_id}
                    className={`flex relative justify-between items-center pl-2 pr-1 mb-1 rounded-md text-sm transition-colors duration-200 group ${getThreadItemClasses(
                      item,
                    )}`}
                    onClick={() => {
                      handleThreadClick(
                        item.thread_id,
                        item.thread,
                        item.bot_role,
                        item,
                      );
                    }}>
                    {renamingThreadId === item.thread_id ? (
                      <input
                        type="text"
                        value={newThreadName}
                        onChange={(e) => setNewThreadName(e.target.value)}
                        onBlur={() =>
                          submitRename(item.thread_id, item.bot_role)
                        }
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          submitRename(item.thread_id, item.bot_role)
                        }
                        className="absolute left-0 z-10 w-full p-2 font-semibold text-red-500 border-none rounded-sm outline-none ring-1 ring-red-500 dark:text-red-400 dark:ring-red-400 bg-slate-100 dark:bg-slate-900"
                        autoFocus
                      />
                    ) : (
                      <p
                        className={`${getLinkClasses(
                          item.thread_id,
                        )} truncate`}>
                        {item.name || `Name: ${item.thread_id}`}
                      </p>
                    )}

                    <div className="relative">
                      {!isDraftThreadId(item.thread_id) ? (
                        <button
                          ref={buttonRef}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDropdownOpen(item.thread_id);
                            setRenamingThreadId(null);
                          }}
                          className="p-2 focus:outline-none ">
                          <Ellipsis
                            size={16}
                            strokeWidth={2.5}
                            className={`opacity-0 hover:text-gray-950 dark:hover:text-gray-50 group-hover:opacity-100 ${
                              dropdownOpen === item.thread_id && "opacity-100"
                            }`}
                          />
                        </button>
                      ) : (
                        <div className="w-8 h-8 bg-transparent" />
                      )}

                      {dropdownOpen === item.thread_id && (
                        <div
                          className="absolute z-40 w-48 p-2 font-normal rounded-md shadow-md shadow-black/25 dark:bg-slate-800 ring-1 ring-slate-300 dark:ring-slate-600 bg-slate-100 -right-12 sm:right-0"
                          ref={dropdownRef}>
                          <button
                            className="flex items-center w-full gap-2 p-2 text-sm text-left rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700"
                            onClick={(e) => {
                              handleButtonClick(e, () =>
                                handleRename(item.thread_id),
                              );
                              setDropdownOpen(null);
                            }}>
                            <Pencil
                              size={16}
                              strokeWidth={2.2}
                            />
                            Rename
                          </button>
                          {items.length > 0 && (
                            <button
                              className="flex items-center w-full gap-2 p-2 text-sm text-left text-red-500 rounded-lg dark:text-red-400 hover:bg-slate-300 dark:hover:bg-slate-700"
                              onClick={(e) =>
                                handleButtonClick(e, () =>
                                  handleDelete(
                                    item.thread_id,
                                    item.name,
                                    item.bot_role,
                                  ),
                                )
                              }>
                              <Trash2
                                size={16}
                                strokeWidth={2.2}
                              />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </div>
            ),
        )}
      </ul>
      <div className="sticky bottom-0 z-20 flex flex-col items-start justify-start gap-2 px-2 pt-2 pb-4 text-sm font-bold transition-colors duration-100 bg-slate-200 dark:bg-slate-900">
        <Toggle
          label="Dark Mode"
          isEnabled={darkMode}
          onToggle={() => setDarkMode(!darkMode)}
        />
        <div className="flex w-full items-center px-2.5 justify-between">
          <p className="">
            {userLevel ? (userLevel === "free" ? "Free" : "Premium") : ""}
          </p>
          <button
            className="px-2 py-1 text-green-700 rounded-full cursor-pointer dark:text-green-400 dark:ring-green-400 dark:hover:bg-green-900 hover:bg-green-200 ring-1 ring-green-700"
            onClick={openModal}>
            Change Plan
          </button>
        </div>

        <div className="w-full h-0.5 bg-slate-400 rounded-full mt-2" />
        <div
          className={`flex items-center w-full gap-2 p-2 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700 cursor-pointer ${
            profileDropdown && "bg-slate-300 dark:bg-slate-700"
          }`}
          onClick={toggleButton}>
          <div
            className="relative flex items-center justify-center w-8 h-8 transition-colors duration-100 bg-green-400 border rounded-full cursor-default border-slate-700 dark:bg-green-800 dark:border-slate-300"
            ref={buttonRef}>
            {user ? user.first_name?.charAt(0) : "A"}
            {profileDropdown && (
              <div
                ref={dropdownRef}
                className="absolute z-50 w-56 p-2 font-normal -translate-x-3 rounded shadow-md bottom-2 left-2 ring-1 dark:ring-slate-600 ring-slate-300 bg-slate-100 shadow-black/25 -translate-y-1/3 dark:bg-slate-800">
                <div className="flex items-center w-full gap-2 p-2 text-left text-gray-600 dark:text-gray-400">
                  <CircleUser
                    className="min-w-4"
                    size={16}
                    strokeWidth={2.2}
                  />
                  <span className="overflow-hidden text-ellipsis">
                    {user.email}
                  </span>
                </div>
                <button
                  className="flex items-center w-full gap-2 p-2 text-sm text-left rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700"
                  onClick={handleChangePassword}>
                  <KeyRound
                    size={16}
                    strokeWidth={2.2}
                  />
                  Change Password
                </button>
                <button
                  onClick={openModal}
                  className="flex items-center w-full gap-2 p-2 text-sm text-left rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700">
                  <Sparkle
                    size={16}
                    strokeWidth={2.2}
                  />
                  Change Plan
                </button>
                <button
                  onClick={logout}
                  className="flex items-center w-full gap-2 p-2 text-sm text-left text-red-700 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700 dark:text-red-400">
                  <LogOut
                    size={16}
                    strokeWidth={2.2}
                  />
                  Logout
                </button>
              </div>
            )}
          </div>
          <span>{user && user.first_name + " " + user.last_name}</span>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, isEnabled, onToggle }) {
  return (
    <div className="flex items-center justify-between w-full px-2.5 py-2">
      <span>{label}</span>
      <button
        onClick={onToggle}
        className={`flex items-center w-[32px] rounded-full bg-slate-700 p-1`}>
        <span
          className={`w-3 h-3 transform rounded-full shadow-md transition-transform duration-300 ease-in-out ${
            isEnabled
              ? "translate-x-3 bg-green-500"
              : "translate-x-0 bg-slate-400"
          }`}
        />
      </button>
    </div>
  );
}
